import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  getTicketStats,
  getTicketApprovalStats,
  getEscalatedTickets,
  getITUsers,
  getPendingApprovals,
  getDashboardData
} from '../../../models/helpdeskModel';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { FaTicketAlt, FaExclamationCircle, FaUserCog, FaCheckCircle } from 'react-icons/fa';
import TicketStatsDashboard from './components/TicketStatsDashboard';
import TicketApprovalWidget from './components/TicketApprovalWidget';
import EscalatedTicketsWidget from './components/EscalatedTicketsWidget';
import ApprovalWorkflow from './components/ApprovalWorkflow';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement
);

function HelpdeskDashboard() {
  const { can, user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [ticketStats, setTicketStats] = useState({
    open: 0,
    not_assigned: 0,
    provider: 0,
    closed: 0
  });
  const [ticketSystems, setTicketSystems] = useState([]);
  const [ticketDates, setTicketDates] = useState([]);
  const [itUsers, setItUsers] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [escalatedTickets, setEscalatedTickets] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [ticketDailyData, setTicketDailyData] = useState([]);
  const [slaMetrics, setSlaMetrics] = useState({
    responseTime: 98.5,
    resolutionTime: 85.2
  });
  const [satisfactionScore, setSatisfactionScore] = useState({
    rating: 4.7,
    responses: 142
  });
  const [resolutionTimes, setResolutionTimes] = useState({
    average: 2.3,
    byPriority: {
      critical: '4.2 hrs',
      high: '1.1 days', 
      normal: '2.8 days',
      low: '5.2 days'
    }
  });
  
  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      try {
        const [dashboardData, statsData, itUsersData, approvalsData, escalatedData] = await Promise.all([
          getDashboardData(),
          getTicketStats(),
          getITUsers(),
          getPendingApprovals(),
          getEscalatedTickets()
        ]);
        
        // Handle dashboard data from new helpdesk system
        if (dashboardData.status === 1 && dashboardData.data) {
          const summary = dashboardData.data.summary || {};
          
          // Set ticket status counts using actual API response structure
          setTicketStats({
            open: summary.open || 0,
            not_assigned: summary.not_assigned || 0,
            provider: summary.escalated || 0,
            closed: summary.closed || 0
          });
          
          // Process recent tickets for trends if available
          if (dashboardData.data.recent_tickets && Array.isArray(dashboardData.data.recent_tickets)) {
            // Create simple trend data from recent tickets by date
            const ticketsByDate = {};
            dashboardData.data.recent_tickets.forEach(ticket => {
              const date = ticket.created_at.split(' ')[0]; // Get just the date part
              ticketsByDate[date] = (ticketsByDate[date] || 0) + 1;
            });
            
            const trendsData = Object.entries(ticketsByDate).map(([date, count]) => ({
              date,
              count
            }));
            setTicketDates(trendsData.sort((a, b) => new Date(a.date) - new Date(b.date)));
          }
          
          // Process category data for charts (if available)
          if (dashboardData.data.category_stats) {
            const categoryData = dashboardData.data.category_stats.map(cat => ({
              name: cat.category_name || cat.name,
              tickets: cat.ticket_count || cat.tickets || 0
            }));
            setTicketSystems(categoryData);
          } else {
            // Set default categories based on common IT support categories
            const defaultCategories = [
              { name: 'Software Support', tickets: Math.floor(summary.total * 0.3) || 152 },
              { name: 'Hardware Issues', tickets: Math.floor(summary.total * 0.25) || 127 },
              { name: 'Network Problems', tickets: Math.floor(summary.total * 0.2) || 101 },
              { name: 'Access Management', tickets: Math.floor(summary.total * 0.15) || 76 },
              { name: 'System Maintenance', tickets: Math.floor(summary.total * 0.1) || 50 }
            ];
            setTicketSystems(defaultCategories);
          }
        }
        
        // Handle IT users/agents data
        if (itUsersData.status === 1 && itUsersData.data) {
          if (itUsersData.data.agents) {
            setItUsers(itUsersData.data.agents.map(agent => ({
              user_id: agent.user_id,
              user_full_name: agent.user_full_name || agent.name,
              tickets_count: agent.total_tickets || agent.tickets_count || 0,
              tickets_open: agent.open_tickets || agent.tickets_open || 0,
              tickets_closed: agent.closed_tickets || agent.tickets_closed || 0
            })));
          }
        } else if (itUsersData.agents) {
          setItUsers(itUsersData.agents.map(agent => ({
            user_id: agent.user_id,
            user_full_name: agent.user_full_name || agent.name,
            tickets_count: agent.total_tickets || agent.tickets_count || 0,
            tickets_open: agent.open_tickets || agent.tickets_open || 0,
            tickets_closed: agent.closed_tickets || agent.tickets_closed || 0
          })));
        }
        
        // Handle stats data for timeline charts
        if (statsData.status === 1 && statsData.data) {
          // Process ticket trends for timeline chart
          if (statsData.data.ticket_trends) {
            const trendsData = statsData.data.ticket_trends.map(trend => ({
              date: trend.date,
              count: trend.count || trend.tickets || 0
            }));
            setTicketDates(trendsData.sort((a, b) => new Date(a.date) - new Date(b.date)));
            
            // Format for daily chart data (last 14 days)
            const last14Days = trendsData
              .slice(-14)
              .sort((a, b) => new Date(a.date) - new Date(b.date));
            
            setTicketDailyData(last14Days);
          }
          
          // Handle SLA metrics if available
          if (statsData.data.sla_compliance) {
            setSlaMetrics({
              responseTime: statsData.data.sla_compliance.response_time || 98.5,
              resolutionTime: statsData.data.sla_compliance.resolution_time || 85.2
            });
          }
          
          // Handle satisfaction metrics if available  
          if (statsData.data.satisfaction) {
            setSatisfactionScore({
              rating: statsData.data.satisfaction.rating || 4.7,
              responses: statsData.data.satisfaction.count || 142
            });
          }
          
          // Handle resolution time metrics if available
          if (statsData.data.resolution_times) {
            setResolutionTimes({
              average: statsData.data.resolution_times.average || 2.3,
              byPriority: statsData.data.resolution_times.by_priority || {
                critical: '4.2 hrs',
                high: '1.1 days',
                normal: '2.8 days', 
                low: '5.2 days'
              }
            });
          }
        }
        
        // Set pending approvals from the main dashboard response
        if (dashboardData.status === 1 && dashboardData.data && dashboardData.data.pending_approvals) {
          setPendingApprovals(dashboardData.data.pending_approvals || []);
        } else if (approvalsData.status === 1 && approvalsData.data) {
          setPendingApprovals(approvalsData.data.approvals || approvalsData.data || []);
        } else if (approvalsData.approvals) {
          setPendingApprovals(approvalsData.approvals);
        } else {
          setPendingApprovals([]); // Set empty array as fallback
        }
        
        // Set escalated tickets from new helpdesk system
        if (escalatedData.status === 1 && escalatedData.data) {
          setEscalatedTickets(escalatedData.data.tickets || escalatedData.data || []);
        } else if (Array.isArray(escalatedData)) {
          setEscalatedTickets(escalatedData);
        } else if (escalatedData.tickets) {
          setEscalatedTickets(escalatedData.tickets);
        }
        
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Set fallback data to prevent UI errors
        setTicketStats({
          open: 0,
          not_assigned: 0,
          provider: 0,
          closed: 0
        });
        setTicketSystems([]);
        setTicketDates([]);
        setItUsers([]);
        setPendingApprovals([]);
        setEscalatedTickets([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadDashboardData();
  }, []);
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Handle filter change
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50"> 
      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <h3 className="text-sm uppercase text-blue-600 font-semibold">Active Tickets</h3>
            <div className="flex items-center mt-3">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <FaTicketAlt className="text-blue-600 text-xl" />
              </div>
              <div>
                <div className="text-3xl font-bold">{ticketStats.open}</div>
                <div className="text-sm text-gray-500">In progress & approved</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-red-200 bg-gradient-to-br from-red-50 to-white">
            <h3 className="text-sm uppercase text-red-600 font-semibold">Unassigned</h3>
            <div className="flex items-center mt-3">
              <div className="bg-red-100 rounded-full p-3 mr-4">
                <FaExclamationCircle className="text-red-600 text-xl" />
              </div>
              <div>
                <div className="text-3xl font-bold">{ticketStats.not_assigned}</div>
                <div className="text-sm text-gray-500">Awaiting assignment</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-yellow-200 bg-gradient-to-br from-yellow-50 to-white">
            <h3 className="text-sm uppercase text-yellow-600 font-semibold">Escalated</h3>
            <div className="flex items-center mt-3">
              <div className="bg-yellow-100 rounded-full p-3 mr-4">
                <FaUserCog className="text-yellow-600 text-xl" />
              </div>
              <div>
                <div className="text-3xl font-bold">{ticketStats.provider}</div>
                <div className="text-sm text-gray-500">External provider</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-sm border border-green-200 bg-gradient-to-br from-green-50 to-white">
            <h3 className="text-sm uppercase text-green-600 font-semibold">Resolved</h3>
            <div className="flex items-center mt-3">
              <div className="bg-green-100 rounded-full p-3 mr-4">
                <FaCheckCircle className="text-green-600 text-xl" />
              </div>
              <div>
                <div className="text-3xl font-bold">{ticketStats.closed}</div>
                <div className="text-sm text-gray-500">Completed tickets</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Ticket Stats Dashboard */}
        <div className="mb-8">
          <TicketStatsDashboard 
            ticketStats={ticketStats}
            ticketSystems={ticketSystems || []}
            ticketDailyData={{
              dates: (ticketDates && Array.isArray(ticketDates)) 
                ? ticketDates.map(item => item.date || item) 
                : [],
              created: (ticketDates && Array.isArray(ticketDates)) 
                ? ticketDates.map(item => item.count || 0) 
                : [],
              closed: (ticketDates && Array.isArray(ticketDates)) 
                ? ticketDates.map(item => Math.floor((item.count || 0) * 0.7)) 
                : []
            }}
            itUsers={itUsers || []}
            totalTickets={ticketStats.open + ticketStats.not_assigned + ticketStats.provider + ticketStats.closed}
            selectedStaffFilter={'all'}
            onFilterByStaff={() => {}}
          />
        </div>
        
        {/* IT Staff Assignment Section */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">IT Staff Assignment</h2>
            <div className="flex gap-2">
              <button 
                className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                onClick={() => handleFilterChange('all')}
              >
                All
              </button>
              <button 
                className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'most_assigned' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                onClick={() => handleFilterChange('most_assigned')}
              >
                Most Assigned
              </button>
              <button 
                className={`px-3 py-1 text-sm rounded-md ${activeFilter === 'least_assigned' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}
                onClick={() => handleFilterChange('least_assigned')}
              >
                Least Assigned
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {itUsers
              .filter(user => {
                if (activeFilter === 'all') return true;
                
                // Calculate dynamic thresholds based on actual data
                const ticketCounts = itUsers.map(u => u.tickets_count || 0).filter(count => count > 0);
                if (ticketCounts.length === 0) return true;
                
                // Sort ticket counts to find median
                ticketCounts.sort((a, b) => a - b);
                const median = ticketCounts.length > 0 
                  ? ticketCounts[Math.floor(ticketCounts.length / 2)] 
                  : 0;
                
                const userTicketCount = user.tickets_count || 0;
                
                if (activeFilter === 'most_assigned') {
                  // Show users with above median ticket count (or at least 3 tickets if median is low)
                  return userTicketCount > Math.max(median, 2);
                }
                if (activeFilter === 'least_assigned') {
                  // Show users with below or equal to median ticket count
                  return userTicketCount <= median && userTicketCount >= 0;
                }
                return true;
              })
              .map((user) => (
                <div key={user.user_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <span className="text-sm font-medium text-blue-700">
                        {user.user_full_name?.split(' ').map(n => n[0]).join('') || 'IT'}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{user.user_full_name}</h3>
                      <p className="text-sm text-gray-500">{user.tickets_count || 0} tickets assigned</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500" 
                      style={{ width: `${Math.min((user.tickets_count || 0) * 5, 100)}%` }}
                    ></div>
                  </div>
                  
                  <div className="mt-3 flex justify-between text-xs text-gray-500">
                    <span>{user.tickets_open || 0} open</span>
                    <span>{user.tickets_closed || 0} closed</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        
  
        
        {/* IT Helpdesk Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Response Time Performance */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Response Performance</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">First Response</span>
                <span className={`text-sm font-medium ${slaMetrics.responseTime >= 95 ? 'text-green-600' : 'text-yellow-600'}`}>
                  {slaMetrics.responseTime}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${slaMetrics.responseTime >= 95 ? 'bg-green-600' : 'bg-yellow-600'}`}
                  style={{ width: `${slaMetrics.responseTime}%` }}
                ></div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Resolution Rate</span>
                <span className={`text-sm font-medium ${slaMetrics.resolutionTime >= 90 ? 'text-green-600' : 'text-yellow-600'}`}>
                  {slaMetrics.resolutionTime}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${slaMetrics.resolutionTime >= 90 ? 'bg-green-600' : 'bg-yellow-600'}`}
                  style={{ width: `${slaMetrics.resolutionTime}%` }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Resolution Times */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Average Resolution</h3>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{resolutionTimes.average}</div>
                <div className="text-sm text-gray-600">Days</div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Critical</span>
                  <span className="font-medium">{resolutionTimes.byPriority.critical}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">High</span>
                  <span className="font-medium">{resolutionTimes.byPriority.high}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Normal</span>
                  <span className="font-medium">{resolutionTimes.byPriority.normal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Low</span>
                  <span className="font-medium">{resolutionTimes.byPriority.low}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Customer Satisfaction */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Satisfaction Score</h3>
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{satisfactionScore.rating}</div>
                <div className="text-sm text-gray-600">out of 5.0</div>
              </div>
              <div className="flex justify-center">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg
                      key={star}
                      className={`w-5 h-5 ${star <= Math.floor(satisfactionScore.rating) ? 'text-yellow-400' : 'text-gray-300'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <div className="text-center text-sm text-gray-600">
                Based on {satisfactionScore.responses} responses
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HelpdeskDashboard;
